import React, { useState, useEffect } from 'react';
import { Sidebar, CustomText, PrimaryCta } from '../../../components';

/**
 * ManageSectionSidebar - Modal sidebar for managing analytics sections
 * Features:
 * 1. Custom values for metric cards (editable with API update)
 * 2. Section visibility toggles for share link
 * 
 * @param {boolean} isOpen - Controls sidebar visibility
 * @param {function} onClose - Callback to close the sidebar
 * @param {object} currentMetricValues - Current values from API for metric cards
 * @param {object} sectionVisibility - Current visibility state of sections
 * @param {function} onSaveCustomValues - Callback when saving custom metric values
 * @param {function} onSaveVisibility - Callback when saving visibility settings
 * @param {boolean} isLoading - Loading state for save operations
 */

// Define editable metrics - key matches what OverviewSection expects
const EDITABLE_METRICS = [
    { key: 'views', label: 'Views' },
    { key: 'likes', label: 'Likes' },
    { key: 'comments', label: 'Comments' },
    { key: 'er_avg', label: 'E.R. (Avg.)' },
    { key: 'er_video', label: 'E.R. (Video)' },
    { key: 'er_static', label: 'E.R. (Static)' },
    { key: 'total_engagements', label: 'Total Engagements' },
    { key: 'cpe', label: 'CPE' },
    { key: 'cpv', label: 'CPV' },
    { key: 'impressions', label: 'Impressions' },
    { key: 'reach', label: 'Reach' },
    { key: 'share', label: 'Share' },
    { key: 'saves', label: 'Saves' },
    { key: 'views_subscriber_ratio', label: 'Views/Subscriber Ratio' },
    { key: 'profile_engagements', label: 'Profile Engagements' },
    { key: 'replies', label: 'Replies' },
    { key: 'submissions', label: 'Submissions' },
    { key: 'remixes', label: 'Remixes' },
    { key: 'conversions', label: 'Conversions' },
    { key: 'sticker_taps', label: 'Sticker Taps' },
    { key: 'planned_cpv', label: 'Planned CPV' },
    { key: 'planned_views', label: 'Planned Views' },
    { key: 'installs', label: 'Installs' },
    { key: 'registration', label: 'Registration' },
    { key: 'deposit', label: 'Deposit' },
    { key: 'total_clicks', label: 'Total Clicks' },
];

// Define toggleable sections for share visibility
const SECTIONS = [
    { key: 'overview', label: 'Overview', description: 'Statistics overview with metric cards' },
    { key: 'timeline', label: 'Timeline', description: 'Content posting timeline chart' },
    { key: 'topPerformers', label: 'Top Performers', description: 'Top performing content cards' },
    { key: 'influencers', label: 'Influencers', description: 'Influencers table' },
    { key: 'content', label: 'Content', description: 'Content gallery grid' },
];

const ManageSectionSidebar = ({
    isOpen,
    onClose,
    sectionVisibility = {},
    onSaveVisibility,
    isLoading = false,
    campaignType,
}) => {
    // Local state for visibility toggles
    const [localVisibility, setLocalVisibility] = useState({
        overview: true,
        timeline: true,
        topPerformers: true,
        influencers: true,
        content: true,
    });
    const [hasChanged, setHasChanged] = useState(false);

    // Initialize local state from props when sidebar opens
    useEffect(() => {
        if (isOpen) {
            setLocalVisibility({
                overview: sectionVisibility?.overview !== false,
                timeline: sectionVisibility?.timeline !== false,
                topPerformers: sectionVisibility?.topPerformers !== false,
                influencers: sectionVisibility?.influencers !== false,
                content: sectionVisibility?.content !== false,
            });
            setHasChanged(false);
        }
    }, [isOpen, sectionVisibility]);

    // Handle visibility toggle
    const handleVisibilityToggle = (key) => {
        setLocalVisibility(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
        setHasChanged(true);
    };

    // Save changes
    const handleSave = () => {
        if (!hasChanged) return;
        onSaveVisibility?.(JSON.stringify(localVisibility));
    };

    // Sidebar header
    const sidebarHeader = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CustomText p text="Manage Sections" />
        </div>
    );

    // Sidebar footer
    const sidebarFooter = () => (
        <div className="manage-section-footer-buttons">
            <PrimaryCta
                text="Save Changes"
                onClick={handleSave}
                isLoading={isLoading}
                disabled={!hasChanged || isLoading}
                fullWidth100
                className="manage-section-footer-btn"
            />
        </div>
    );

    // Filter sections based on campaign type
    const availableSections = SECTIONS.filter(section => {
        if (campaignType === 'content') {
            return section.key !== 'overview' && section.key !== 'topPerformers';
        }
        return true;
    });

    return (
        <Sidebar
            isOpen={isOpen}
            onClose={onClose}
            Header={sidebarHeader}
            Footer={sidebarFooter}
            sidebarWidth="450px"
            headerAlignment="left"
        >
            <div className="manage-section-sidebar-content">
                <div className="manage-section-panel">
                    <p className="manage-section-panel-description">
                        Choose which sections to show when sharing analytics with clients.
                    </p>
                    <div className="manage-section-card-grid">
                        {availableSections.map((section) => {
                            const isVisible = localVisibility[section.key];
                            return (
                                <div
                                    key={section.key}
                                    className={`manage-section-card ${isVisible ? 'is-visible' : 'is-hidden'}`}
                                >
                                    <div>
                                        <div className="manage-section-card-title">{section.label}</div>
                                        <div className="manage-section-card-description">
                                            {section.description}
                                        </div>
                                    </div>
                                    <div className="manage-section-card-controls">
                                        <span className="manage-section-card-state">
                                            {isVisible ? 'Visible on share link' : 'Hidden from share link'}
                                        </span>
                                        <label className="manage-section-toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={isVisible}
                                                onChange={() => handleVisibilityToggle(section.key)}
                                            />
                                            <span
                                                className="manage-section-toggle-thumb"
                                                aria-hidden="true"
                                            ></span>
                                        </label>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Sidebar>
    );
};

export default ManageSectionSidebar;
